import { create } from 'zustand';
import { ChatState, Workspace } from '../types';

// Helper to get current user ID from auth storage
const getCurrentUserId = (): string | null => {
  try {
    const authData = localStorage.getItem('oquis-auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed?.state?.user?.userId || null;
    }
    return null;
  } catch {
    return null;
  }
};

// Helper to get user-specific storage key
const getUserKey = (key: string): string => {
  const userId = getCurrentUserId();
  return userId ? `oquis-${userId}-${key}` : `oquis-guest-${key}`;
};

// Helper to get workspace from localStorage (user-specific)
export const getStoredWorkspace = (): Workspace | null => {
  try {
    const stored = localStorage.getItem(getUserKey('workspace'));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Helper to get last session ID from localStorage (user-specific)
export const getStoredSessionId = (): string | null => {
  try {
    return localStorage.getItem(getUserKey('session-id'));
  } catch {
    return null;
  }
};

// Helper to save workspace to localStorage (user-specific)
const saveWorkspace = (workspace: Workspace | null): void => {
  try {
    if (workspace) {
      localStorage.setItem(getUserKey('workspace'), JSON.stringify(workspace));
    } else {
      localStorage.removeItem(getUserKey('workspace'));
      localStorage.removeItem(getUserKey('session-id'));
    }
  } catch {
    // Ignore storage errors
  }
};

// Helper to save session ID to localStorage (user-specific)
const saveSessionId = (sessionId: string | null): void => {
  try {
    if (sessionId) {
      localStorage.setItem(getUserKey('session-id'), sessionId);
    } else {
      localStorage.removeItem(getUserKey('session-id'));
    }
  } catch {
    // Ignore storage errors
  }
};

export const useChatStore = create<ChatState>()((set) => ({
  currentSession: null,
  sessions: [],
  selectedWorkspace: getStoredWorkspace(),
  lastSessionId: getStoredSessionId(),

  setCurrentSession: (session) =>
    set((state) => {
      const next = typeof session === 'function' ? session(state.currentSession) : session;
      if (!next) {
        saveSessionId(null);
        return { currentSession: null };
      }

      const safeMessages = Array.isArray(next.messages) ? next.messages : [];

      // Store session ID in localStorage for reload (user-specific)
      if (next.id) {
        saveSessionId(next.id);
      }

      return {
        currentSession: {
          ...next,
          messages: safeMessages,
        },
      };
    }),

  setSessions: (sessions) =>
    set({
      sessions: Array.isArray(sessions)
        ? sessions.map((s) => ({
          ...s,
          messages: Array.isArray(s.messages) ? s.messages : [],
        }))
        : [],
    }),

  setSelectedWorkspace: (workspace) => {
    // Store in localStorage (user-specific)
    saveWorkspace(workspace);
    return set({ selectedWorkspace: workspace });
  },

  addMessage: (message) =>
    set((state) => {
      if (!state.currentSession) return state;

      const safeMessages = Array.isArray(state.currentSession.messages)
        ? state.currentSession.messages
        : [];

      return {
        currentSession: {
          ...state.currentSession,
          messages: [...safeMessages, message],
          lastActivityAt: new Date().toISOString(),
        },
      };
    }),

  // Reload workspace and session from user-specific storage (call after login)
  reloadFromStorage: () =>
    set({
      selectedWorkspace: getStoredWorkspace(),
      lastSessionId: getStoredSessionId(),
    }),

  // Clear all state (call when user changes to prevent seeing other user's data)
  clearState: () =>
    set({
      currentSession: null,
      sessions: [],
      selectedWorkspace: null,
      lastSessionId: null,
    }),
}));
